import {
  EnvironmentTypeEnum,
  INTEGRATION_STATE,
  PERK_BRAND_LOGO,
  PERK_STATUS,
  SORT_ORDER,
  USER_TYPE,
} from '@app-enums/index';
import { ISortOptions } from '@app-types/general';
import { IPerk } from '@app-types/queryMutationTypes/perk';
import { Loc } from '@app-types/queryMutationTypes/profile';
import { IUser } from '@app-types/queryMutationTypes/user';
import { COMMUNICATION_CONFIG } from '@app-utils/constants/generic';
import moment from 'moment';
import {
  getObjectKey,
  isArray,
  isObject,
  calcCrow,
  generateCouponCode,
} from 'zaions-tool-kit';
import { ObjectId } from 'mongodb';

export const getCompanyText = (userData: IUser): string => {
  if (getObjectKey(userData, 'type', '') === USER_TYPE.merchant)
    return 'Business';
  if (getObjectKey(userData, 'type', '') === USER_TYPE.student_body)
    return 'College';
  if (getObjectKey(userData, 'type', '') === USER_TYPE.employer)
    return 'Company';

  if (
    getObjectKey(userData, 'type', '') === USER_TYPE.employee &&
    isObject(getObjectKey(userData, 'employerId', false), true)
  ) {
    if (
      userData?.employerId?.type === USER_TYPE.employer ||
      userData?.employerId?.type === USER_TYPE.merchant ||
      (userData?.employerId?.type === USER_TYPE.student_body &&
        userData?.isSuperUser)
    )
      return 'Member of staff';
    return 'Student';
  } else {
    return 'Company';
  }
};

export const distanceBusiness = (
  loc?: Array<Loc>,
  curr?: Loc
): number | false => {
  const location: Array<number> = [];
  isArray(loc) &&
    loc?.forEach((_loc) => {
      const distance = calcCrow(
        curr?.lat ?? 0,
        curr?.lng ?? 0,
        _loc?.lat ? _loc?.lat : 0,
        _loc?.lng ? _loc?.lng : 0
      );
      location.push(distance);
    });
  location.sort((a: number, b: number) => {
    return a - b;
  });
  return isArray(location) && location[0];
};

export const getNearestSortedBusiness = async ({
  curr,
  locBusiness,
  nearby,
  region,
  sort,
}: {
  curr: Loc;
  locBusiness: Array<IUser>;
  nearby: any;
  sort: any;
  region: string;
}): Promise<Array<any>> => {
  const businessCoordinates = [];

  if (locBusiness) {
    for (const cord of locBusiness) {
      if (
        (cord.profile && cord?.profile?.locations?.length === 0) ||
        !cord.profile
      ) {
        const _distance =
          nearby && sort && sort.distance === -1
            ? 0
            : nearby
            ? 100000000
            : null;
        businessCoordinates.push({
          ...cord,
          distance: Number(_distance),

          region,
        });
      } else {
        const distance = distanceBusiness(
          cord.profile && cord.profile.locations,
          curr
        );
        const cordd = { ...cord, distance, region };
        businessCoordinates.push(cordd);
      }
    }
  }

  if (nearby || (sort && sort.distance === 1)) {
    businessCoordinates.sort((a, b) => {
      return Number(a?.distance) - Number(b?.distance);
    });
  }
  if (nearby && sort && sort.distance === -1) {
    businessCoordinates.sort((a, b) => {
      return Number(b?.distance) - Number(a?.distance);
    });
  }

  return businessCoordinates;
};

export const isMerchantEmployee = (user: IUser): boolean => {
  return (
    isObject(user?.employerId) && user?.employerId?.type === USER_TYPE.merchant
  );
};

export const isTestAccount = (
  user: IUser,
  testAccountEmails: string[]
): boolean => {
  const userEmail = getObjectKey(user, 'email');
  if (isArray(testAccountEmails, true) && userEmail) {
    const result = testAccountEmails.includes(userEmail);
    return result;
  } else {
    return false;
  }
};

export const isScheduledPerk = (perk: IPerk | null): boolean => {
  const scheduled =
    !perk?.alwaysDisplay &&
    moment(perk?.displayFromDate?.toString()).isAfter(moment());

  return scheduled;
};

export const isScheduledPerkV2 = (perk: IPerk | null): boolean => {
  if (!perk) return false; // If the perk is null or undefined, it's not scheduled.

  const todayStart = moment().startOf('day'); // Today's date at 00:00:00
  const displayFromDate = perk.displayFromDate
    ? moment(perk.displayFromDate.toString()) // Ensure moment can handle the date
    : null;

  const isScheduled =
    !perk.alwaysDisplay &&
    displayFromDate &&
    displayFromDate.isAfter(todayStart); // Compare against the start of today.

  return Boolean(isScheduled);
};

export const isEndingSoonPerk = (perk: IPerk | null): boolean => {
  if (perk?.alwaysDisplay || !perk?.displayUntilDate) return false;
  let dateTwoWeeksLater = moment(moment()).add(14, 'days');
  const ending =
    !perk?.alwaysDisplay &&
    moment(perk?.displayUntilDate?.toString()).isBefore(dateTwoWeeksLater);
  return ending;
};

export const isEndedPerk = (perk: IPerk | null): boolean => {
  if (perk?.alwaysDisplay || !perk?.displayUntilDate) return false;
  const ended =
    !perk?.alwaysDisplay &&
    moment(perk?.displayUntilDate?.toString()).isBefore(moment());
  return ended;
};

export const currentPerkStatus = (perk: IPerk | null): PERK_STATUS => {
  let status = PERK_STATUS.LIVE;

  if (perk?.status === PERK_STATUS.DRAFT) {
    status = PERK_STATUS.DRAFT;
  } else if (perk?.status === PERK_STATUS.PENDINGAPPROVAL) {
    status = PERK_STATUS.PENDINGAPPROVAL;
  } else if (perk?.status === PERK_STATUS.DISAPPROVED) {
    status = PERK_STATUS.DISAPPROVED;
  } else if (isEndedPerk(perk)) {
    status = PERK_STATUS.ENDED;
  } else if (isScheduledPerk(perk)) {
    status = PERK_STATUS.SCHEDULED;
  } else if (isEndingSoonPerk(perk)) {
    status = PERK_STATUS.ENDINGSOON;
  }
  return status;
};

export const checkIntegrationIsAllowedAndItsState = (
  userData: IUser
): {
  msTeamsIntegrationIsAllowed: boolean;
  msTeamsIsConnected: boolean;
  slackIntegrationIsAllowed?: boolean;
  slackIsConnected?: boolean;
  stripeIntegrationIsAllowed?: boolean;
  slackIntegrationIsEnabled?: boolean;
  microsoftTeamsIntegrationIsEnabled?: boolean;
  userCanSendSlackNotifications?: boolean;
  userCanSendMSTeamsNotifications?: boolean;
} => {
  const userType = getObjectKey(userData, 'type');
  const isEmployee = userType === USER_TYPE.employee;
  const isSuperUserEmployee =
    isEmployee && getObjectKey(userData, 'isSuperUser');
  const isSimpleEmployee = isEmployee && !isSuperUserEmployee;

  // Check if integrations are allowed
  let msTeamsIntegrationIsAllowed = userData?.allowMSTeamsConnection ?? false;
  let slackIntegrationIsAllowed = userData?.allowSlackConnection;
  let stripeIntegrationIsAllowed = userData?.allowStripeConnection;

  if (isSuperUserEmployee || isSimpleEmployee) {
    const orgOwnerUser = userData?.employerId;
    msTeamsIntegrationIsAllowed = orgOwnerUser?.allowMSTeamsConnection!;
    slackIntegrationIsAllowed = orgOwnerUser?.allowSlackConnection;
    stripeIntegrationIsAllowed = orgOwnerUser?.allowStripeConnection;
  }

  // Check integrations connection status
  const msTeamsIsConnected = userData?.msTeamsConDetails != null;
  const slackIsConnected =
    (userData?.slackConnectionDetails?.app_id ?? null) != null;

  const slackIntegrationIsEnabled =
    userData?.slackIntegrationState &&
    userData?.slackIntegrationState === INTEGRATION_STATE.ENABLED;
  const microsoftTeamsIntegrationIsEnabled =
    userData?.microsoftTeamsIntegrationState &&
    userData?.microsoftTeamsIntegrationState === INTEGRATION_STATE.ENABLED;

  const userCanSendSlackNotifications =
    slackIntegrationIsEnabled && slackIsConnected;
  const userCanSendMSTeamsNotifications =
    msTeamsIsConnected && microsoftTeamsIntegrationIsEnabled;

  return {
    msTeamsIntegrationIsAllowed,
    msTeamsIsConnected,
    slackIntegrationIsAllowed,
    slackIsConnected,
    stripeIntegrationIsAllowed,
    slackIntegrationIsEnabled,
    microsoftTeamsIntegrationIsEnabled,
    userCanSendSlackNotifications,
    userCanSendMSTeamsNotifications,
  };
};

export const getDefaultBrandLogoType = (_userData: IUser): PERK_BRAND_LOGO => {
  let brandLogoType = PERK_BRAND_LOGO.NO_LOGO;

  // if current user is merchant or merchant employee use profile logo as default
  if (
    (getObjectKey(_userData, 'type', '') === USER_TYPE.merchant &&
      _userData.brandLogo) ||
    (isMerchantEmployee(_userData) &&
      getObjectKey(getObjectKey(_userData, 'employerId'), 'brandLogo'))
  ) {
    brandLogoType = PERK_BRAND_LOGO.USE_PROFILE_LOGO;
  }

  return brandLogoType;
};

// Just for reference, will be removed after testing getPhoneAttempts
export const getPhoneAttemptsV1 = (
  user: IUser
): {
  invalid: boolean;
  timeDiff: string;
} => {
  const attemptDate = new Date(user.lastPhoneAttempt!);
  const futureDate = new Date(
    attemptDate.getTime() +
      COMMUNICATION_CONFIG.PHONE_ATTEMPTS_TIME_LIMIT_IN_MINUTES * 60000
  );
  const nowDate = new Date();
  const diff = Math.abs(
    new Date(nowDate).getTime() - new Date(futureDate).getTime()
  );
  const timeDiffInMinutes = Math.floor(diff / 1000 / 60);
  const timeDiffInSeconds = Math.floor(diff / 1000);
  let timeDiff: string;
  if (timeDiffInMinutes === 1) {
    timeDiff = timeDiffInMinutes.toString() + ' minute';
  } else if (timeDiffInMinutes < 1) {
    timeDiff = timeDiffInSeconds.toString() + ' seconds';
  } else {
    timeDiff = timeDiffInMinutes.toString() + ' minutes';
  }

  return { invalid: nowDate < futureDate, timeDiff };
};

/**
 * Calculates the time difference for phone attempts and checks if the attempt is invalid.
 *
 * @param {Object} user - The user object containing the last phone attempt timestamp.
 * @param {string | Date} user.lastPhoneAttempt - The timestamp of the user's last phone attempt.
 * @returns {{ invalid: boolean; timeDiff: string }} - An object indicating whether the attempt is invalid and the time difference.
 */
export const getPhoneAttempts = (
  user: IUser
): { invalid: boolean; timeDiff: string } => {
  const attemptDate = new Date(user?.lastPhoneAttempt!);
  const futureDate = new Date(
    attemptDate.getTime() +
      COMMUNICATION_CONFIG.PHONE_ATTEMPTS_TIME_LIMIT_IN_MINUTES * 60 * 1000
  );
  const nowDate = new Date();

  const diffInMillis = Math.abs(futureDate.getTime() - nowDate.getTime());

  const diffInSeconds = Math.floor(diffInMillis / 1000);

  const diffInMinutes = Math.floor(diffInSeconds / 60);

  let timeDiff: string;
  if (diffInMinutes > 1) {
    timeDiff = `${diffInMinutes} minutes`;
  } else if (diffInMinutes === 1) {
    timeDiff = `${diffInMinutes} minute`;
  } else {
    timeDiff = `${diffInSeconds} seconds`;
  }

  return { invalid: nowDate < futureDate, timeDiff };
};

export const getSortQuery = (sort: ISortOptions): any => {
  if (Object.keys(sort || {}).length) {
    const result: any = {};
    result[sort.order ? sort.field : '_id'] =
      sort.order === SORT_ORDER.ASCEND ? 1 : -1;

    return result;
  } else {
    return { _id: -1 };
  }
};

export const isSuperUser = (user: IUser): boolean => {
  return (user.type === USER_TYPE.employer && user.isSuperUser) ?? false;
};

export const generatePfCouponCode = (): string => {
  return generateCouponCode({ parts: 1, partLen: 6 }).toString().toUpperCase();
};

export const getPerkViewUrl = ({
  perkId,
  userSubDomain,
  frontEndUrl,
}: {
  perkId: string;
  userSubDomain: string;
  frontEndUrl?: string;
}) => {
  return `${userSubDomain || frontEndUrl}/perk/${perkId}`;
};

export const generateVanityUrl = (text: string): string | undefined => {
  if (!text) return;
  return text
    .replace(/[^a-zA-Z0-9 ]/gi, '')
    .trim()
    .replace(/ /g, '-');
};

/**
 * Helper function to convert a value to MongoDB ObjectId.
 * @param value - The value to be converted to ObjectId.
 * @returns The ObjectId or null if the value is invalid.
 */
export const toMongoObjectId = (
  value: string | undefined | null | ObjectId
): ObjectId | null => {
  try {
    return value ? ObjectId.createFromHexString(String(value)) : null;
  } catch (error) {
    console.error(`Invalid ObjectId value: ${value}`, error);
    return null;
  }
};

export const getEmployerId = (
  user: IUser<EnvironmentTypeEnum.backend>
): ObjectId | null => {
  const isEmployee = user.type === USER_TYPE.employee;

  return isEmployee
    ? toMongoObjectId(user?.employerId)
    : toMongoObjectId(user._id);
};

export const addTimeToDate = (
  time = 3,
  unit: moment.DurationInputArg2 = 'minutes',
  date: Date = new Date()
): string => {
  return new Date(moment(date).add(time, unit).toString()).getTime().toString();
};
